import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { GiftBowIcon } from '@/atoms/Icons/GiftBowIcon'
import { GiftIcon } from '@/atoms/Icons/GiftIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { MapPinIcon } from '@/atoms/Icons/MapPinIcon'
import { ParentalControlsFullIcon } from '@/atoms/Icons/ParentalControlsFullIcon'
import { ParentalControlsOutlineIcon } from '@/atoms/Icons/ParentalControlsOutlineIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { WatchIcon } from '@/atoms/Icons/WatchIcon'
import { CaptionSM, LabelMD, LabelXS } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { WatchlistButton } from '@/molecules/WatchlistButton'
import { isStreaming } from '@/services/ProjectPhaseService'
import { Project, ResumeWatchingData, VideoMetaData } from '@/services/ProjectsService'
import { useWatchProjectContext } from '@/services/ProjectsService/WatchProjectProvider'
import { ShopifyConfiguration } from '@/services/ShopifyConfigurationService/ShopifyConfigurationService'
import { useUser } from '@/services/UserService'
import { Media } from '@/utils/EpisodeUtil'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage } from '@/utils/local-storage/local-storage'
import { FAMILY_GUIDE_DISMISSED } from '@/utils/local-storage/local-storage-keys'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useWatchProjectCTAs } from '@/views/WatchProjectView/useWatchProjectCTAs'

const FamilyGuideModal = dynamic(
  () => import('./FamilyGuideModal').then((mod) => ({ default: mod.FamilyGuideModal })),
  { ssr: false },
)

interface WatchHeroCTAsProps {
  projectData: Project
  bonusVideos?: VideoMetaData[]
  ctaPath?: string
  ctaText?: string
  ctaClassName?: string
  isGuildMemberContent?: boolean
  isGeoBlockedProject?: boolean
  shopifyConfig?: ShopifyConfiguration | null
  pifEnabled?: boolean
  isGuildMember?: boolean
  shareCtaPath?: string | null
  watchableReasons?: Array<{
    __typename: string
    startDatetime?: string | null
  } | null> | null
  guildCodes?: string[]
  areGuildTicketsAvailable?: boolean
  activeSweepstake?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  showParentalControls?: boolean
  movieSpecialMedias: Media[]
  onPrimaryCtaGuidChange?: (guid: string | null) => void
  resumeWatchingData?: ResumeWatchingData
}

export const WatchHeroCTAs: React.FC<WatchHeroCTAsProps> = ({
  projectData,
  bonusVideos,
  ctaPath,
  ctaText = 'Watch Sneak Peek',
  ctaClassName,
  isGuildMemberContent = false,
  isGeoBlockedProject = false,
  shopifyConfig,
  pifEnabled = false,
  isGuildMember = false,
  shareCtaPath,
  watchableReasons = [],
  guildCodes = [],
  areGuildTicketsAvailable = false,
  activeSweepstake = false,
  parentalControlsApplied = 0,
  parentalControlsTotal = 0,
  showParentalControls = false,
  movieSpecialMedias,
  onPrimaryCtaGuidChange,
  resumeWatchingData,
}) => {
  const { t } = useTranslate('watch')
  const { isLoggedIn } = useUser()
  const { ticketStatus, watchableContent, guildPermissions } = useWatchProjectContext()
  const track = useSafeTrack()
  const router = useSafeRouter()
  const [isFamilyGuideOpen, setIsFamilyGuideOpen] = useState(false)
  const hasAutoOpenedFamilyGuide = useRef(false)

  const defaultGuildPermissions = {
    hasFreeTickets: false,
    hasMerchDiscount: false,
    hasEarlyAccess: false,
    canVote: false,
    adFree: false,
    publicViewingRight: false,
    limitedAds: false,
  }

  const {
    primaryCtaPath,
    primaryCtaText,
    primaryCtaIcon,
    secondaryCtaPath,
    secondaryCtaText,
    secondaryCtaIcon,
    trailer,
    primaryCtaGuid,
  } = useWatchProjectCTAs({
    projectData,
    bonusVideos,
    isGuildMember,
    ctaPath,
    ctaText,
    isGuildMemberContent,
    t,
    guildPermissions: guildPermissions ?? defaultGuildPermissions,
    watchableReasons,
    guildCodes,
    areGuildTicketsAvailable,
    activeSweepstake,
    isGeoBlockedProject,
    movieSpecialMedias,
    resumeWatchingData,
    ticketStatus,
    streamingContent: watchableContent,
  })

  useEffect(() => {
    if (onPrimaryCtaGuidChange) {
      onPrimaryCtaGuidChange(primaryCtaGuid)
    }
  }, [primaryCtaGuid, onPrimaryCtaGuidChange])

  useEffect(() => {
    if (
      !hasAutoOpenedFamilyGuide.current &&
      showParentalControls &&
      parentalControlsTotal > 0 &&
      !getBooleanFromLocalStorage(FAMILY_GUIDE_DISMISSED, false)
    ) {
      hasAutoOpenedFamilyGuide.current = true
      setIsFamilyGuideOpen(true)
    }
  }, [showParentalControls, parentalControlsTotal])

  const handleButtonClick = useCallback(
    (path: string) => {
      path.includes('/guild/join')
        ? track('Guild Watch Join Clicked')
        : track('Show Page CTA Clicked', { slug: projectData?.slug })
    },
    [projectData?.slug, track],
  )

  const hasStreamableContent = isStreaming(watchableContent)

  const canShowPif = useMemo(() => {
    if (hasStreamableContent) return false
    const projectsWithPif = [
      slugs.dryBarToons,
      slugs.homesteadSeries,
      slugs.homesteadFamilySurvival,
      slugs.riotAndTheDanceSeries,
      slugs.tuttleTwinsMinisodes,
    ]
    return Boolean(pifEnabled || projectsWithPif.some((p) => projectData?.slug === p))
  }, [hasStreamableContent, pifEnabled, projectData?.slug])

  const pifLink = useMemo(() => {
    if (projectData?.slug === slugs.dryBarToons) {
      return `${paths.payItForward.index}/${slugs.dryBar}`
    }
    if (projectData?.slug === slugs.homesteadSeries || projectData?.slug === slugs.homesteadFamilySurvival) {
      return `${paths.payItForward.index}/${slugs.homestead}`
    }
    if (projectData?.slug === slugs.riotAndTheDanceSeries) {
      return `${paths.payItForward.index}/${slugs.riot}`
    }
    if (projectData?.slug === slugs.tuttleTwinsMinisodes) {
      return `${paths.payItForward.index}/${slugs.tuttleTwins}`
    }
    if (projectData?.slug) {
      return `${paths.payItForward.index}/${projectData.slug}`
    }
    return `${paths.payItForward.index}/${slugs.forYourConsideration}`
  }, [projectData?.slug])

  const handleShareClick = useCallback(() => {
    track('Content Link Project Share Film Clicked')
  }, [track])

  const handleGiftGuildClick = useCallback(() => {
    track('Gift Guild CTA Clicked')
  }, [track])

  const additionalActions = [
    {
      label: t('shareFreeAccess', 'Share Free Access'),
      icon: <ShareOutlineIcon color="white" size={20} />,
      href: shareCtaPath ?? '',
      canShow: Boolean(isGuildMember && shareCtaPath && projectData?.title?.isGuildShareAvailable),
      onClick: handleShareClick,
    },
    {
      label: t('trailer', 'Trailer'),
      icon: <WatchIcon color="white" size={20} />,
      href:
        trailer && trailer?.guid
          ? trailer?.isBonus
            ? `/watch/${projectData.slug}/videos/bonus/${trailer.guid}`
            : `/watch/${projectData.slug}/episode/${trailer?.guid}`
          : '',
      canShow:
        trailer &&
        trailer?.guid &&
        !(primaryCtaText?.toLowerCase()?.includes('trailer') || secondaryCtaText?.toLowerCase()?.includes('trailer')),
      onClick: null,
    },
    {
      label: t('payItForward', 'Pay it Forward'),
      icon: <PayItForwardOutlineIcon color="white" size={20} />,
      href: pifLink,
      canShow: canShowPif,
      onClick: null,
    },
    {
      label: t('shopGifts', 'Shop Gifts'),
      icon: <GiftBowIcon color="white" size={20} />,
      href: shopifyConfig?.storeHref
        ? shopifyConfig.storeHref
        : projectData?.projectType === 'special'
        ? paths.external.shop.dryBar
        : paths.external.shop.index,
      canShow: true,
      onClick: null,
    },
    {
      label: t('giftTheGuild', 'Gift the Guild'),
      icon: <GiftIcon color="white" size={20} />,
      href: isGuildMember ? paths.guild.gift.index : paths.external.giftGuild,
      canShow: Boolean(isGuildMember),
      onClick: handleGiftGuildClick,
    },
  ]

  const updatedPrimaryCtaText = useMemo(() => {
    if (primaryCtaText !== t('joinToWatch', 'Join To Watch')) {
      return primaryCtaText
    }

    if (isLoggedIn) {
      return t('unlockAndWatch', 'Unlock and Watch')
    }

    return t('joinToWatch', 'Join To Watch')
  }, [isLoggedIn, t, primaryCtaText])

  const handleParentalControlsClick = () => {
    track('Clicked Parental Controls Brand Landing CTA')
    if (parentalControlsApplied > 0 && primaryCtaPath) {
      const url = new URL(primaryCtaPath, window.location.origin)
      url.searchParams.set('show-parental-controls', 'true')
      router.push(`${url.pathname}${url.search}`)
    } else {
      setIsFamilyGuideOpen(true)
    }
  }

  return (
    <>
      <div className="flex min-h-[64px] flex-col gap-2 md:flex-row md:gap-4">
        {/* Hidden spacer to maintain height when no CTAs are present */}
        {!primaryCtaPath && !secondaryCtaPath && <div className="invisible mb-4 h-0 py-[14px]" aria-hidden="true" />}
        {primaryCtaPath && primaryCtaText && (
          <LinkButton
            href={primaryCtaPath ?? ''}
            variant="white"
            disabled={isGeoBlockedProject}
            onClick={() => handleButtonClick(primaryCtaPath)}
            className={classNames(
              'flex items-center gap-[6px] py-[14px] px-6 w-full md:w-fit max-h-12 rounded-xl mb-4',
              ctaClassName,
            )}
          >
            {primaryCtaIcon && determineButtonIcon(primaryCtaIcon, 'core-gray-950')}
            <LabelMD weight="semibold" className="mt-[2.25px]" color="core-gray-950">
              {updatedPrimaryCtaText}
            </LabelMD>
          </LinkButton>
        )}
        {secondaryCtaPath && secondaryCtaText && (
          <LinkButton
            href={secondaryCtaPath ?? ''}
            variant="core-gray-800"
            onClick={() => handleButtonClick(secondaryCtaPath)}
            className={classNames(
              'flex items-center gap-[6px] py-[14px] px-6 w-full md:w-fit max-h-12 rounded-xl mb-4 bg-core-gray-500 backdrop-blur-3xl',
              ctaClassName,
            )}
          >
            {secondaryCtaIcon && determineButtonIcon(secondaryCtaIcon, 'white')}
            <LabelMD weight="semibold" className="mt-[2.25px]">
              {secondaryCtaText}
            </LabelMD>
          </LinkButton>
        )}
        {projectData?.title?.id && projectData?.slug && isLoggedIn && (
          <WatchlistButton titleId={projectData.title.id} />
        )}
      </div>
      <When condition={showParentalControls && parentalControlsTotal > 0}>
        <button
          type="button"
          onClick={handleParentalControlsClick}
          className="mb-4 flex w-full cursor-pointer items-center justify-center gap-2 md:w-auto md:justify-start"
        >
          {parentalControlsApplied > 0 ? (
            <ParentalControlsFullIcon size={18} color="oxide-bright" />
          ) : (
            <ParentalControlsOutlineIcon size={18} color="oxide-bright" />
          )}
          <CaptionSM color="oxide-bright" className="md:photon-caption-md">
            {parentalControlsApplied > 0
              ? t('parentalControlsAppliedCount', '{{applied}} of {{total}} Parental Controls Applied', {
                  applied: parentalControlsApplied,
                  total: parentalControlsTotal,
                })
              : t('parentalControlsAvailable', 'Parental Controls Available')}
          </CaptionSM>
        </button>
        <FamilyGuideModal
          isOpen={isFamilyGuideOpen}
          onClose={() => setIsFamilyGuideOpen(false)}
          slug={projectData?.slug ?? ''}
          projectType={projectData?.projectType}
          watchPath={primaryCtaPath ?? undefined}
        />
      </When>
      <div className="flex flex-row items-start justify-around md:-ml-[12px] md:mb-8 md:justify-start lg:mb-12">
        {additionalActions.map((action, index) => (
          <When condition={action.canShow} key={index}>
            <a
              className={classNames(
                'flex flex-col items-center gap-1 min-w-[76px] mr-2 cursor-pointer group-watch-ctas',
                ctaClassName,
              )}
              href={action.href}
            >
              <span className="cursor-pointer rounded-full bg-core-gray-900 p-[10px] transition-all duration-300 ease-in-out group-watch-ctas-hover:scale-105 group-watch-ctas-hover:bg-core-gray-800">
                {action.icon}
              </span>
              <LabelXS color="core-gray-400" className="flex-wrap text-center">
                {action.label}
              </LabelXS>
            </a>
          </When>
        ))}
      </div>
    </>
  )
}

const determineButtonIcon = (icon: string, color: IconColor) => {
  switch (icon) {
    case 'play':
      return <PlayCircleOutlineIcon size={24} color={color} />
    case 'ticket':
      return <TicketIcon size={24} color={color} />
    case 'gift':
      return <GiftBowIcon size={24} color={color} />
    case 'share':
      return <ShareOutlineIcon size={24} color={color} />
    case 'guild':
      return <GuildIconMono size={24} color={color} />
    case 'geo':
      return <MapPinIcon size={24} color={color} />
    default:
      return null
  }
}
